import React, { FC, useEffect, useRef, useState } from 'react'
import DataField from '../table-header/DataField'
import { Button, CheckboxMark, Input, ThrashBinIcon } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'
import { TableHeaders, TableRow } from '../table/Table'
import { PencilIcon } from '@heroicons/react/24/outline'
import TableHeaderCombobox from '../table-header/TableHeaderCombobox'
import { DispatchSetAction } from '@sistemiv/s-components/dist/esm/types/types'
import { MinusIcon } from '@heroicons/react/24/solid'

type TableProps = {
  headers: TableHeaders[]
  rows: TableRow[]
  selectedRows: TableRow[]
  setSelectedRows: DispatchSetAction<TableRow[]>
  onHeaderFilter?: (field: string, value: any) => void
  refreshTable?: () => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onNameSearch: (value: string) => void
  onValueSearch: (value: string) => void
  filterValues: { [key: string]: any }
}

const Table: FC<TableProps> = ({
  headers,
  rows,
  selectedRows,
  setSelectedRows,
  onEdit,
  onDelete,
  onNameSearch,
  onValueSearch,
  filterValues,
  onHeaderFilter,
}) => {
  const [tableData, setTableData] = useState<TableRow[]>(rows)
  const { t } = useTranslation()
  const [pendingNameSearch, setPendingNameSearch] = useState('')
  const [pendingValueSearch, setPendingValueSearch] = useState('')

  useEffect(() => {
    setTableData(rows)
  }, [rows])

  useEffect(() => {
    const delayTyping = setTimeout(() => {
      onNameSearch(pendingNameSearch)
    }, 500)

    return () => clearTimeout(delayTyping)
  }, [pendingNameSearch, onNameSearch])

  useEffect(() => {
    const delayTyping = setTimeout(() => {
      onValueSearch(pendingValueSearch)
    }, 500)

    return () => clearTimeout(delayTyping)
  }, [pendingValueSearch, onValueSearch])

  const allRowsSelected =
    selectedRows?.length > 0 &&
    rows?.every((row) => selectedRows?.find((selected) => selected['id']?.value === row?.['id'].value))

  return (
    <>
      <table className='min-w-full divide-y divide-gray-200 border-collapse'>
        <thead className='text-sm bg-white'>
          <tr className='border-b border-gray-300'>
            <th className='px-3'>
              <div className='relative flex items-center justify-center '>
                <div className='flex gap-x-2'>
                  <span
                    className={`${selectedRows.length > 0 ? 'border-sky-500' : 'border-gray-300'} ${
                      allRowsSelected ? 'bg-sky-500' : 'bg-white'
                    } w-4.5 h-4.5 border-2 rounded-sm inline-flex items-center justify-center cursor-pointer`}
                    onClick={(e) => {
                      e.preventDefault()
                      if (selectedRows.length === 0) {
                        // setSelectedRows(rows.map((row) => row['id'].value))
                        setSelectedRows(rows)
                      } else {
                        setSelectedRows([])
                      }
                    }}
                  >
                    {allRowsSelected && <CheckboxMark className='w-4.5 h-4.5 fill-white flex-none' />}
                    {selectedRows.length > 0 && !allRowsSelected && (
                      <MinusIcon className='text-sky-500 w-4 h-4 flex-none flex items-start stroke-2' />
                    )}
                  </span>
                  {/* {selectedRows.length > 0 && <span className='font-medium text-sky-500'>{selectedRows.length}</span>} */}
                </div>
              </div>
            </th>
            {headers?.map((header, index) => (
              <th
                data-testid='table-header'
                key={index}
                scope='col'
                className='px-6 py-3 text-left font-normal text-slate-600 capitalize tracking-wider cursor-pointer'
              >
                <div className='inline-flex items-center justify-start gap-x-3 '>{header.label}</div>
              </th>
            ))}
          </tr>
          <tr className='border-b border-gray-300'>
            <th></th>
            {headers?.map((header, index) => (
              <th
                key={index}
                scope='col'
                className='px-6 py-3 text-left font-medium text-gray-500 min-w-[12rem] tracking-wider'
                data-testid='filter'
              >
                {header.searchType === 'search' ? (
                  <div>
                    <Input
                      id={'search'}
                      type={'search'}
                      classes='!rounded-md [&>input]:!border-solid [&>input]:border [&>input]:border-gray-300 focus:[&>input]:border-sky-100 pr-16 max-w-72'
                      placeholder={t('NamedValues.search') as string}
                      value={header.accessor === 'name' ? pendingNameSearch : pendingValueSearch}
                      onChange={(e) =>
                        header.accessor === 'name'
                          ? setPendingNameSearch(e.target.value)
                          : setPendingValueSearch(e.target.value)
                      }
                    />
                  </div>
                ) : header.searchType === 'select' ? (
                  <div>
                    <TableHeaderCombobox
                      options={
                        header.options?.map((h) => {
                          if (typeof h === 'string') {
                            return {
                              type: 'plainText',
                              value: h,
                            }
                          } else if (typeof h === 'object' && h.id) {
                            return {
                              type: 'plainText',
                              id: h.id,
                              value: h.name,
                            }
                          } else {
                            return {
                              type: 'plainText',
                              value: '',
                            }
                          }
                        }) ?? []
                      }
                      value={filterValues[header.accessor] ?? []}
                      selectFor={''}
                      onChange={(val) => onHeaderFilter && onHeaderFilter(header.accessor, val)}
                    />
                  </div>
                ) : (
                  <div></div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='bg-white'>
          {tableData?.map((row, index) => (
            <tr key={index} className='border-b border-gray-300 group hover:bg-sky-50' data-testid='row'>
              {/* <td></td> */}
              <td className='px-3'>
                <BulkCheckbox selectedRows={selectedRows} setSelectedRows={setSelectedRows} row={row} />
              </td>
              {headers?.map((header, index) => {
                const field = header.accessor !== 'actions' ? row[header.accessor] : null
                if (header.accessor === 'actions') {
                  return (
                    <td
                      key={index}
                      className='px-6 py-3 whitespace-nowrap text-sm text-gray-500 text-start'
                      data-testid='table-field'
                    >
                      <div className='flex items-center'>
                        <Button
                          className='!p-0 mr-3'
                          onClick={() => {
                            onEdit(row['id']?.value)
                          }}
                        >
                          <PencilIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                        </Button>
                        <Button
                          className='!p-0'
                          onClick={() => {
                            onDelete(row['id']?.value)
                          }}
                        >
                          <ThrashBinIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                        </Button>
                      </div>
                    </td>
                  )
                } else {
                  return (
                    <td
                      key={index}
                      className='px-6 py-3 whitespace-nowrap text-sm text-gray-500 text-start'
                      data-testid='table-field'
                    >
                      <DataField field={field!} />
                    </td>
                  )
                }
              })}
              <td className='p-3'></td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

const BulkCheckbox: FC<{
  // selectedRows: string[]
  // setSelectedRows: DispatchSetAction<string[]>
  selectedRows: TableRow[]
  setSelectedRows: DispatchSetAction<TableRow[]>
  row: TableRow
}> = ({ selectedRows, setSelectedRows, row }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div
      className='p-3 rounded-full hover:bg-blue-100 flex justify-center items-center transition-all ease-out duration-75'
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        if (inputRef.current) {
          inputRef.current.click()
        }
      }}
    >
      <input
        ref={inputRef}
        type='checkbox'
        // checked={selectedRows.includes(row['id'].value)}
        checked={!!selectedRows.find((selectedRow) => selectedRow['id'].value === row['id'].value)}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          if (e.target.checked) {
            // setSelectedRows((oldRows) => {
            //   return [...oldRows, row['id'].value]
            // })
            setSelectedRows((oldRows) => {
              return [...oldRows, row]
            })
          } else {
            // setSelectedRows((oldRows) => {
            //   return [...oldRows].filter((r) => r !== row['id'].value)
            // })
            setSelectedRows((oldRows) => {
              return [...oldRows].filter((r) => r['id'].value !== row['id'].value)
            })
          }
        }}
        className='cursor-pointer h-4.5 w-4.5 text-sky-500 rounded-sm disabled:cursor-default  disabled:text-gray-300 border-2 border-gray-300 focus:outline-none focus:ring-0 focus:ring-offset-0'
      />
    </div>
  )
}

export default Table
